import { FunctionComponent, ReactNode, useEffect, useState } from 'react';
import { asyncWithLDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import LDClientLib, { LDClient, LDContext, LDEvaluationDetail } from 'launchdarkly-js-client-sdk';
import { datadogRum } from '@datadog/browser-rum';

import { logger } from '../../providers';
import { LAUNCH_DARKLY_CONFIG } from '../../environment/launchDarkly';
import { LAUNCH_DARKLY_ERRORS } from './errors';

export type LDProviderType = FunctionComponent<{ children: ReactNode }>;

const LD_API_TIMEOUT_IN_SECONDS = 10;
const LD_API_TIMEOUT_IN_MILLISECONDS = 10000;
const context = {
  kind: 'user',
  key: LAUNCH_DARKLY_CONFIG.clientSideID,
};

export const getLDClient = (): undefined | LDClient => {
  try {
    const ldClient = LDClientLib.initialize(LAUNCH_DARKLY_CONFIG.clientSideID, context, {
      bootstrap: 'localStorage',
      inspectors: [
        {
          type: 'flag-used',
          name: 'dd-inspector',
          method: (key: string, detail: LDEvaluationDetail) => {
            try {
              datadogRum.addFeatureFlagEvaluation(key, detail.value);
            } catch {
              // noop so that a datadog issue doesnt cause a problem with LD.
            }
          },
        },
      ],
    });

    ldClient.on('error', (error: Error) => {
      logger.error(LAUNCH_DARKLY_ERRORS.initialisation, error);
    });

    ldClient.on('failed', (failure: any) => {
      logger.error(LAUNCH_DARKLY_ERRORS.initialisation, new Error(failure));
    });

    return ldClient;
  } catch {
    logger.error(
      LAUNCH_DARKLY_ERRORS.initialisation,
      new Error('Error creating LaunchDarkly client'),
    );

    return undefined;
  }
};

export const getLaunchDarklyProvider = async (): Promise<LDProviderType | null> => {
  const ldClient = getLDClient();

  try {
    /*
    this always returns a provider.
    we also pass in the client, if the function above returns undefined, the asyncWithLDProvider function 
    will try to create its own.  
    */
    const LaunchDarklyProvider = await asyncWithLDProvider({
      clientSideID: LAUNCH_DARKLY_CONFIG.clientSideID,
      timeout: LD_API_TIMEOUT_IN_SECONDS,
      ldClient,
    });

    return LaunchDarklyProvider;
  } catch (error) {
    logger.error(LAUNCH_DARKLY_ERRORS.provider, error);

    return null;
  }
};

export const useLaunchDarkly = (userContext: LDContext | undefined) => {
  const [isInitialised, setIsInitialised] = useState<boolean>(false);
  const [hasTriedToLoad, setHasTriedToLoad] = useState<boolean>(false);
  const [identified, setIdentified] = useState<boolean>(false);

  // this will be undefined if we failed to launchDarkly.
  const ldClient = useLDClient();
  const flags = useFlags();

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    try {
      ldClient
        .waitForInitialization(LD_API_TIMEOUT_IN_SECONDS)
        .then(() => {
          setIsInitialised(true);
        })
        .catch(() => {
          //again just handling the case where promise may be rejected to prevent app from crashing

          //setting attempted to identify as true as we wont be able identify a user on a client that failed ot initialise
          setHasTriedToLoad(true);
        });
    } catch {
      //we dont care if this fails, just eating any errors to prevent app from crashing.

      //setting attempted to identify as true as we wont be able identify a user on a client that failed ot initialise
      setHasTriedToLoad(true);
    }
  }, [ldClient]);

  useEffect(() => {
    const identify = async () => {
      if (!ldClient) {
        return;
      }
      if (!isInitialised) {
        return;
      }
      if (!userContext) {
        return;
      }
      try {
        await ldClient.identify(userContext);
        setIdentified(true);
        setHasTriedToLoad(true);
      } catch (err) {
        let error: Error;
        if (err instanceof Error) {
          error = err;
        } else {
          error = new Error('Failed to identify user');
        }

        logger.error(LAUNCH_DARKLY_ERRORS.userIdentification, error);
      } finally {
        setHasTriedToLoad(true);
      }
    };

    identify();
  }, [userContext, ldClient, isInitialised]);

  useEffect(() => {
    const timeoutHandle = setTimeout(() => {
      setHasTriedToLoad(true);
    }, LD_API_TIMEOUT_IN_MILLISECONDS);

    return () => {
      clearTimeout(timeoutHandle);
    };
  }, []);

  //if launch darkly fails, the flags object is a proxy passed back from launch darkly that always returns false.
  return { identified, loaded: hasTriedToLoad, flags };
};
